import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Card from "../component/card";
import MainLayout from "../layout/main";
import {
	changePassword,
	getCurrentUser,
	updateUser,
	uploadFile
} from "../service/callAPI";
import { labelStyle } from "../theme/default";
import Schema from "../type/schema";
import { toast } from "react-toastify";
import { handleFileInput } from "../lib/fileInput";
import ValidatorSpan from "../component/validatorSpan";
import Button from "react-bootstrap/Button";

type ProfileInputs = {
	fullName: string;
	phone: string;
	email: string;
	address: string;
	id: string;
	username: string;
	photoUrl: string;
};

type PasswordInputs = {
	currentPassword: string;
	newPassword: string;
	confirmNewPassword: string;
};

export default function ProfilePage() {
	return (
		<MainLayout
			pageTitle="Cài Đặt Cá Nhân"
			title="Cài Đặt Cá Nhân"
			subTitle="Cấu hình tài khoản cá nhân, đổi mật khẩu,..."
		>
			<div className="row">
				<div className="col-sm-6">
					<ProfileForm />
				</div>
				<div className="col-sm-6">
					<PasswordForm />
				</div>
			</div>
		</MainLayout>
	);
}

const ProfileForm = () => {
	const initialConvert = (data: Schema.User): ProfileInputs => {
		return data as unknown as ProfileInputs;
	};

	const finalConvert = (data: ProfileInputs): Schema.User => {
		return data as unknown as Schema.User;
	};

	const { register, handleSubmit, formState, setValue } =
		useForm<ProfileInputs>();

	const { errors, isDirty } = formState;

	const onSubmit: SubmitHandler<ProfileInputs> = async (data) => {
		try {
			const converted = finalConvert(data);
			await updateUser(converted);
			toast.info("Đã cập nhật thông tin thành công");
		} catch (e: any) {
			toast.error(`Lỗi lưu thông tin|${e.toString()}`);
		}
	};

	const loadState = useCallback(async () => {
		try {
			const user = await getCurrentUser();
			const converted = initialConvert(user);
			for (let [key, val] of Object.entries(converted)) {
				setValue(key as any, val);
			}
		} catch (e: any) {
			toast.error(`Lỗi tải user|${e.toString()}`);
		}
	}, [setValue]);

	useEffect(() => {
		loadState();
	}, [loadState]);

	const [photoBase64, setPhotoBase64] = useState("");

	return (
		<Card title="Thông Tin">
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="row">
					<div className="col-sm-6">
						<label className="mt-2 mr-2" style={labelStyle} htmlFor="id">
							ID:
						</label>
						<input readOnly className="form-control" {...register("id")} />
					</div>
					<div className="col-sm-6">
						<label className="mt-2 mr-2" style={labelStyle} htmlFor="username">
							Username:
						</label>
						<input
							readOnly
							className="form-control"
							{...register("username")}
						/>
					</div>
				</div>

				<label className="mt-2" style={labelStyle} htmlFor="fullName">
					Họ tên:
				</label>
				<input
					className="form-control"
					{...register("fullName", {
						required: "Không được để trống trường này"
					})}
				/>
				{errors.fullName && <ValidatorSpan message={errors.fullName.message} />}

				<div className="row">
					<div className="col-sm-6">
						<label className="mt-2" style={labelStyle}>
							Điện thoại:
						</label>
						<input
							className="form-control"
							{...register("phone", {
								required: "Không được để trống trường này"
							})}
						/>
						{errors.phone && <ValidatorSpan message={errors.phone.message} />}

						<label className="mt-2 mr-2" style={labelStyle} htmlFor="address">
							Địa chỉ:
						</label>
						<textarea
							rows={3}
							className="form-control"
							{...register("address")}
						/>
					</div>

					<div className="col-sm-6">
						<label className="mt-2" style={labelStyle} htmlFor="email">
							Email:
						</label>
						<input
							className="form-control"
							{...register("email", {
								required: "Không được để trống trường này"
							})}
						/>
						{errors.email && <ValidatorSpan message={errors.email.message} />}

						{/* <label className="mt-2 mr-2" style={labelStyle}>
							Ảnh đại diện:
						</label>
						<div>
							{photoBase64 && (
								<img
									className="mb-2"
									src={photoBase64}
									style={{ maxHeight: "80px" }}
									alt="Avatar"
								/>
							)}
						</div>
						<input
							className="form-control-file"
							type="file"
							placeholder="Upload File"
							onChange={handleFileInput((base64) => {
								setPhotoBase64(base64);
								uploadFile({
									dir: "/misc",
									base64String: base64
								}).then((response) => setValue("photoUrl", response.url));
							})}
						/> */}
					</div>
				</div>

				<div
					className="form-group mt-4"
					style={{
						textAlign: "center"
					}}
				>
					<button
						className="btn btn-primary m-2"
						type="submit"
						disabled={!isDirty}
					>
						Cập nhật
					</button>
					<button
						className="btn btn-danger m-2"
						onClick={(e) => {
							e.preventDefault();
							loadState();
						}}
					>
						Làm lại
					</button>
				</div>
			</form>
		</Card>
	);
};

const PasswordForm = () => {
	const { register, handleSubmit, watch, formState, reset } =
		useForm<PasswordInputs>();

	const { isDirty, errors } = formState;

	const onSubmit: SubmitHandler<PasswordInputs> = async (data) => {
		try {
			await changePassword(data);
			return toast.info("Đã đổi mật khẩu thành công");
		} catch (e: any) {
			const message = e?.response?.data || e.toString();
			toast.error(message);
		}
	};

	return (
		<Card title="Đổi Mật Khẩu">
			<form onSubmit={handleSubmit(onSubmit)}>
				<label
					className="mt-2 mr-2"
					style={labelStyle}
					htmlFor="currentPassword"
				>
					Mật khẩu hiện tại:
				</label>
				<input
					type="password"
					className="form-control"
					{...register("currentPassword", {
						required: "Không được để trống trường này"
					})}
				/>
				{errors.currentPassword && (
					<ValidatorSpan message={errors.currentPassword.message} />
				)}

				<label className="mt-2 mr-2" style={labelStyle}>
					Mật khẩu mới:
				</label>
				<input
					type="password"
					className="form-control"
					{...register("newPassword", {
						required: "Không được để trống trường này"
					})}
				/>
				{errors.newPassword && (
					<ValidatorSpan message={errors.newPassword.message} />
				)}

				<label className="mt-2 mr-2" style={labelStyle}>
					Xác nhận mật khẩu mới:
				</label>
				<input
					type="password"
					className="form-control"
					{...register("confirmNewPassword", {
						validate: {
							required: (value) =>
								(!!value && true) || "Không được để trống trường này",
							notMatched: (value) =>
								(value === watch("newPassword") && true) ||
								"Nhập lại mật khẩu mới không khớp"
						}
					})}
				/>
				{errors.confirmNewPassword && (
					<ValidatorSpan message={errors.confirmNewPassword.message} />
				)}

				<div
					className="form-group mt-4"
					style={{
						textAlign: "center"
					}}
				>
					<Button
						variant="primary"
						className="m-2"
						type="submit"
						disabled={!isDirty}
					>
						Cập nhật
					</Button>
					<Button
						variant="danger"
						className="m-2"
						onClick={() => {
							reset();
						}}
					>
						Làm lại
					</Button>
				</div>
			</form>
		</Card>
	);
};
